import { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';
import {
  useCalibrateHypothesis,
  useUpsertHypothesis,
} from '../../hooks/useRevenueHypothesis.js';
import Button from '../Button.jsx';

const MONTH_LABELS_FULL = [
  'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre',
];
const MONTH_LABELS_SHORT = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const DEFAULT_COEFS = Array(12).fill(1.0);

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

function buildInitialForm(hypothesis) {
  if (!hypothesis) {
    return {
      name: 'Hypothèse principale',
      baseline: '',
      growth: '0',
      pessimiste: '-15',
      optimiste: '15',
      coefs: DEFAULT_COEFS.map(String),
    };
  }
  return {
    name: hypothesis.name ?? 'Hypothèse principale',
    baseline: hypothesis.baseline_cents_monthly != null
      ? String(hypothesis.baseline_cents_monthly / 100)
      : '',
    growth: String(hypothesis.growth_pct_yearly ?? 0),
    pessimiste: String(hypothesis.pessimiste_variance_pct ?? -15),
    optimiste: String(hypothesis.optimiste_variance_pct ?? 15),
    coefs: (hypothesis.seasonality_coefs ?? DEFAULT_COEFS).map(String),
  };
}

function validate(form) {
  const errors = {};
  const b = parseFloat(form.baseline);
  if (!form.baseline || isNaN(b) || b <= 0) errors.baseline = 'Baseline requise (> 0)';
  return errors;
}

/**
 * Mini-visualisation live des barres de saisonnalite.
 */
function LiveSeasonalityPreview({ coefs }) {
  const vals = coefs.map((c) => parseFloat(c) || 0);
  const maxVal = Math.max(...vals, 0.01);

  return (
    <div className="flex items-end gap-0.5 h-8 mt-1">
      {vals.map((v, i) => {
        const h = Math.max(2, Math.round((v / maxVal) * 28));
        return (
          <div key={i} style={{ flex: 1 }} className="flex flex-col items-center gap-0">
            <div
              className="w-full rounded-t bg-blue-400 transition-all"
              style={{ height: `${h}px` }}
            />
            <span className="text-[8px] text-slate-400 leading-none select-none">
              {MONTH_LABELS_SHORT[i]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Modal d'edition complete de l'hypothese de revenus.
 *
 * Props :
 *   isOpen     — boolean
 *   onClose    — callback
 *   hypothesis — objet hypothese existant (ou null si creation)
 */
export default function RevenueHypothesisEditModal({ isOpen, onClose, hypothesis }) {
  const [form, setForm] = useState(() => buildInitialForm(hypothesis));
  const [errors, setErrors] = useState({});

  const calibrate = useCalibrateHypothesis();
  const upsert = useUpsertHypothesis();

  // Reset quand la modal s'ouvre ou que l'hypothese change
  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialForm(hypothesis));
      setErrors({});
      upsert.reset?.();
    }
  }, [isOpen, hypothesis?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Verrou scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Fermeture clavier
  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose?.(); }
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function setCoef(index, value) {
    setForm((prev) => {
      const coefs = [...prev.coefs];
      coefs[index] = value;
      return { ...prev, coefs };
    });
  }

  async function handleCalibrateAndFill() {
    try {
      const result = await calibrate.mutateAsync({ year: 2025 });
      setForm((prev) => ({
        ...prev,
        baseline: String(result.baseline_cents_monthly / 100),
        coefs: result.seasonality_coefs.map(String),
      }));
    } catch {
      // calibrate.error exposé dans le rendu
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const coefs = form.coefs.map((c) => {
      const v = parseFloat(c);
      return isNaN(v) ? 1.0 : v;
    });

    const body = {
      id: hypothesis?.id,
      name: form.name.trim() || 'Hypothèse principale',
      baseline_cents_monthly: Math.round(parseFloat(form.baseline) * 100),
      growth_pct_yearly: parseFloat(form.growth) || 0,
      pessimiste_variance_pct: parseFloat(form.pessimiste) || -15,
      optimiste_variance_pct: parseFloat(form.optimiste) || 15,
      seasonality_coefs: coefs,
      // start_year = année de référence du baseline. Par défaut année courante - 1
      // (le baseline correspond au CA moyen de l'année calibrée). Indispensable
      // pour que growth_pct s'applique : projection N → years_offset = N - start_year.
      start_year: hypothesis?.start_year ?? (new Date().getFullYear() - 1),
    };

    await upsert.mutateAsync(body);
    onClose?.();
  }

  if (!isOpen) return null;

  const isDisabled = upsert.isPending || calibrate.isPending;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Modifier l'hypothese de revenus"
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-white shadow-2xl max-h-[94dvh] overflow-y-auto sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg sm:rounded-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-6">
          <h2 className="text-base font-semibold text-slate-900">
            {hypothesis ? 'Modifier l’hypothese CA' : 'Configurer l’hypothese CA'}
          </h2>
          <button
            onClick={onClose}
            className="ml-auto rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-5 sm:px-6 space-y-5">
          {/* Bouton calibration rapide */}
          <button
            type="button"
            onClick={handleCalibrateAndFill}
            disabled={isDisabled}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-60"
          >
            <RefreshCw size={12} className={calibrate.isPending ? 'animate-spin' : ''} />
            {calibrate.isPending ? 'Calcul en cours…' : 'Reinitialiser depuis 2025 (auto-calibration)'}
          </button>
          {calibrate.error && (
            <p className="text-xs text-red-600">{calibrate.error.message}</p>
          )}

          {/* Nom */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700">Nom</label>
            <input
              type="text"
              className={inputClass}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Hypothese principale"
              disabled={isDisabled}
            />
          </div>

          {/* Baseline + Croissance */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">
                Baseline mensuelle (€) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                className={inputClass}
                value={form.baseline}
                onChange={(e) => setField('baseline', e.target.value)}
                min="0.01"
                step="0.01"
                placeholder="25000"
                disabled={isDisabled}
              />
              {errors.baseline && (
                <p className="text-xs text-red-600">{errors.baseline}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">
                Croissance (%/an)
              </label>
              <input
                type="number"
                className={inputClass}
                value={form.growth}
                onChange={(e) => setField('growth', e.target.value)}
                step="0.1"
                placeholder="0"
                disabled={isDisabled}
              />
            </div>
          </div>

          {/* Variances */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">
                Variance pessimiste (%)
              </label>
              <input
                type="number"
                className={inputClass}
                value={form.pessimiste}
                onChange={(e) => setField('pessimiste', e.target.value)}
                step="1"
                placeholder="-15"
                disabled={isDisabled}
              />
              <p className="text-[11px] text-slate-400">Negatif ex. -15</p>
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">
                Variance optimiste (%)
              </label>
              <input
                type="number"
                className={inputClass}
                value={form.optimiste}
                onChange={(e) => setField('optimiste', e.target.value)}
                step="1"
                placeholder="15"
                disabled={isDisabled}
              />
              <p className="text-[11px] text-slate-400">Positif ex. +15</p>
            </div>
          </div>

          {/* Coefficients saisonnalite */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-slate-700">
              Saisonnalite (coefficients ×)
            </label>
            <LiveSeasonalityPreview coefs={form.coefs} />
            <div className="grid grid-cols-3 gap-2 mt-2">
              {MONTH_LABELS_FULL.map((month, i) => (
                <div key={i} className="space-y-0.5">
                  <label className="block text-[10px] text-slate-500">{month}</label>
                  <input
                    type="number"
                    className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50 tabular-nums"
                    value={form.coefs[i]}
                    onChange={(e) => setCoef(i, e.target.value)}
                    step="0.01"
                    min="0"
                    placeholder="1.00"
                    disabled={isDisabled}
                    aria-label={`Coefficient ${month}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Erreur serveur */}
          {upsert.error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {upsert.error.message}
            </p>
          )}

          {/* Boutons */}
          <div className="flex gap-2 pt-1 pb-safe">
            <Button
              type="submit"
              loading={upsert.isPending}
              disabled={isDisabled}
              className="flex-1"
            >
              {hypothesis ? 'Enregistrer' : 'Creer'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isDisabled}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
